// LandingWedge — Two audience-specific marketing pages (vendors, PMs).
// Each has a hero + 4 pain-point → solution cards + demo callout + CTA to
// /signup?wedge=vendors|pm. Keeps the codebase minimal: one component,
// two data configs, two routes.
import { Link } from "react-router-dom";
import {
  Wrench, Buildings, PlugsConnected, Camera, ClockCounterClockwise,
  ArrowRight, CheckCircle, ChartLineUp, PaperPlaneTilt, LockKey,
  Repeat, ShieldCheck, ShieldWarning, Package, Snowflake,
} from "@phosphor-icons/react";

const WEDGES = {
  vendors: {
    tag: "For maintenance vendors",
    accent: "cyan",
    hero: {
      title: "Turn Maintenex jobs into a dispatch board your crew actually uses.",
      sub: "Photos, status, and invoices sync back automatically. Stop clicking through vendor portals — start closing more WOs.",
      cta: "Try free for 7 days",
      cta_href: "/signup?wedge=vendors",
    },
    pains: [
      { icon: PlugsConnected, k: "Every CMMS speaks Field Radar",
        p: "Maintenex, ServiceChannel, Corrigo, FMX — forward the WO email, we parse it into your Dispatch board in 2 seconds." },
      { icon: Camera, k: "Photos actually make it back",
        p: "Techs snap on-site photos. Field Radar posts them back to the source CMMS so the property owner never wonders \"did they even show up?\"" },
      { icon: ClockCounterClockwise, k: "SLA timers you can't miss",
        p: "Every bridged WO shows an SLA countdown. Green until 30 min before deadline, then it screams at you." },
      { icon: ChartLineUp, k: "Win your next contract with a demo, not a promise",
        p: "Pitch: \"Our shop responds 2x faster than any other vendor on your panel.\" Then show the video. Panels notice." },
    ],
    proof: [
      "Works with any CMMS that emails you WOs (Maintenex, ServiceChannel, Corrigo, FMX, HappyCo, and 15+ more)",
      "20+ integrations wired day one — 5 more added per quarter based on customer requests",
      "Full 2-way sync in v2 (Q1 2026) — status, photos, invoices",
    ],
  },
  pm: {
    tag: "For property management shops",
    accent: "fuchsia",
    hero: {
      title: "Run every landlord, every unit, every WO from one operating layer.",
      sub: "The tools you use don't change — AppFolio, Buildium, Yardi keep running. Field Radar wraps them so nothing falls through the cracks.",
      cta: "Try free for 14 days",
      cta_href: "/signup?wedge=pm",
    },
    pains: [
      { icon: Buildings, k: "Every property, one dashboard",
        p: "Chew Flats has its own hub. Raza Homes has its portal. Owners see only their own. Techs see only their WOs. Everyone stays in their lane." },
      { icon: Wrench, k: "Turn maintenance from a text-storm into a system",
        p: "Tenants report anonymously (FERPA-safe for student housing). WOs auto-triage by severity. Vendors get dispatched by first-available team logic." },
      { icon: LockKey, k: "Two-way entry permission links",
        p: "Text tenants a link. They grant entry + pick a window. You propose windows back. Everyone's on the same page — no more \"we thought you were coming Wednesday\"." },
      { icon: PaperPlaneTilt, k: "Leasing agents your owner will love",
        p: "Give each leasing agent a private portal. They see vacant units + codes, check in on showings, report issues with photos — all auto-notified to the owner." },
    ],
    proof: [
      "Multi-tenant from day 1 — each landlord scoped to their own units, PII masking for student housing",
      "Landlord portal built-in — owners see live activity, sign alarm addendums, invite their own leasing team",
      "Battle-tested on a real PMR shop running Chew Flats (43 LaSalle units) + Raza Homes + 4sBay",
    ],
  },
  handyman: {
    tag: "For handymen & small shops",
    accent: "cyan",
    hero: {
      title: "Every job. Every photo. Every dollar. On one screen.",
      sub: "Built for the one-truck shop that quotes on a napkin. Track jobs, log photos, get paid same-day — without leaving the driveway.",
      cta: "Try free for 14 days",
      cta_href: "/signup?wedge=handyman",
    },
    pains: [
      { icon: Wrench, k: "Never lose another quote",
        p: "Snap a photo at walkthrough, dictate the estimate. Field Radar remembers every job you bid — chase the ones that ghost you in one tap." },
      { icon: Camera, k: "Photos organized without lifting a finger",
        p: "Every photo auto-tags to the right job. Before/during/after sorted by time. Print a proof sheet before invoicing." },
      { icon: PaperPlaneTilt, k: "Get paid same-day",
        p: "Close a job → text a Stripe link → cash hits your account in 20 min. No net-30 headaches, no chasing checks." },
      { icon: ChartLineUp, k: "Bid smarter than the bigger shops",
        p: "See average time + cost on your past jobs by type. Quote a bathroom drywall repair with data, not gut. Bigger shops don't have your history." },
    ],
    proof: [
      "Voice-first job logging — dictate on the drive between jobs, WO writes itself",
      "Auto-mileage log for tax deduction — usually $2-4k/yr you're leaving on the table",
      "Stripe payments in 2 taps · QuickBooks sync coming Q1",
    ],
  },
  hvac: {
    tag: "For HVAC contractors",
    accent: "cyan",
    hero: {
      title: "Ride the surge without burning out your crew.",
      sub: "Maintenance contracts, seasonal service, callbacks — Field Radar keeps every unit + every filter + every warranty on a leash. No more scrambling on the first 90-degree day.",
      cta: "Try free for 14 days",
      cta_href: "/signup?wedge=hvac",
    },
    pains: [
      { icon: Repeat, k: "Recurring maintenance on autopilot",
        p: "Set filter changes every 3 mo, coil cleans every 6 mo, tune-ups every fall. Field Radar dispatches them automatically. No more \"we forgot the filter.\"" },
      { icon: ShieldCheck, k: "Warranty tracker on every install",
        p: "Every install has a warranty clock. WO on a 4-yr-old condenser? System flags \"still under LG warranty — call before dispatching.\" Recover $$$ every month." },
      { icon: ClockCounterClockwise, k: "Callback rate you can see",
        p: "Every WO called back within 30 days = a callback. Tech-level dashboard shows who's coming back to their own work. Fixes quality quietly." },
      { icon: ChartLineUp, k: "Weather-aware dispatching",
        p: "Cold snap Tuesday? System auto-front-loads heat-pump calls. 95°F Friday? AC installs get priority. You react before your phones start ringing." },
    ],
    proof: [
      "EPA 608 refrigerant logging on every WO — audit-ready for compliance",
      "Truck inventory tracker (refrigerant + fittings + capacitors) — never leave shop without what you need",
      "Recurring maintenance calendar — set once, dispatches forever",
    ],
  },
  plumbing: {
    tag: "For plumbers",
    accent: "cyan",
    hero: {
      title: "After-hours calls without after-hours chaos.",
      sub: "Auto-triage the water heater from the burst pipe. Route emergencies to whoever's on-call. Ditch the 2 AM group text.",
      cta: "Try free for 14 days",
      cta_href: "/signup?wedge=plumbing",
    },
    pains: [
      { icon: ShieldWarning, k: "On-call rotation that self-manages",
        p: "Set the weekly schedule once. Emergencies auto-page whoever's on-call. Nobody's phone rings on their week off." },
      { icon: Camera, k: "Water damage documentation ironclad",
        p: "Every leak call gets a mandatory before/after photo prompt + moisture reading log. Insurance disputes end in your favor." },
      { icon: Package, k: "Parts on the truck, tracked",
        p: "Each truck has a running inventory. WO tagged \"wax ring\" auto-checks Nate's truck — dispatches him if he has one, sends to Home Depot pickup if empty." },
      { icon: ChartLineUp, k: "Restoration company handoffs, clean",
        p: "Water damage triggers auto-notify to your ServiceMaster / Servpro partner with the leak site photos + tech notes. Referral cash while they get the mitigation work." },
    ],
    proof: [
      "Emergency Radar — after-hours calls auto-triaged + routed to on-call tech",
      "Callback tracker at the tech level — quality without micromanagement",
      "Home Depot pickup pre-orders — one tap, ready-in-30-min, no aisle-hunting",
    ],
  },
  roofing: {
    tag: "For roofers",
    accent: "cyan",
    hero: {
      title: "Insurance-grade documentation before you leave the roof.",
      sub: "Every ladder photo, hail-strike marker, and shingle count logged automatically. Weather-aware dispatch. Estimating that beats Xactimate on speed.",
      cta: "Try free for 14 days",
      cta_href: "/signup?wedge=roofing",
    },
    pains: [
      { icon: Camera, k: "Storm damage documentation, court-ready",
        p: "Every hail strike, every soft spot, every missing shingle timestamped + GPS-tagged. Insurance adjusters can't dispute what they can see." },
      { icon: ChartLineUp, k: "Estimate on-site in 5 minutes",
        p: "AR tape measure snaps the roof pitch + area from your phone. Cross-references your material cost history → out the door with a signed estimate." },
      { icon: Snowflake, k: "Weather-aware scheduling",
        p: "Rain forecast Wednesday? Roof jobs auto-shuffle to Mon/Tue. Storm chase? System auto-batches every roof in the impact zone into a dispatch route." },
      { icon: ShieldCheck, k: "Fall-safety proof on every job",
        p: "Before any roof step, phone auto-prompts a \"harness on\" photo. One tap. OSHA + insurance love this. Premiums quietly drop 5-10%." },
    ],
    proof: [
      "Storm-radar integration — flag every property in a hail impact zone for follow-up",
      "Xactimate-compatible estimate exports (v2)",
      "AR measure + area calc — no more manual pitch math",
    ],
  },
};

export default function LandingWedge({ wedgeKey = "vendors" }) {
  const w = WEDGES[wedgeKey] || WEDGES.vendors;
  const other = wedgeKey === "vendors" ? WEDGES.pm : WEDGES.vendors;
  const otherHref = wedgeKey === "vendors" ? "/for-property-managers" : "/vendors";
  const otherLabel = wedgeKey === "vendors" ? "Property manager instead? →" : "Vendor instead? →";

  const accentBg = w.accent === "cyan" ? "bg-cyan-600 hover:bg-cyan-700" : "bg-fuchsia-600 hover:bg-fuchsia-700";
  const accentText = w.accent === "cyan" ? "text-cyan-600" : "text-fuchsia-600";
  const accentPill = w.accent === "cyan" ? "bg-cyan-50 text-cyan-700" : "bg-fuchsia-50 text-fuchsia-700";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid={`landing-${wedgeKey}`}>
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-black text-lg tracking-tight" data-testid="landing-logo">
            Field <span className={accentText}>Radar</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to={otherHref} className="text-xs font-bold text-slate-500 hover:text-slate-900" data-testid="landing-switch-audience">
              {otherLabel}
            </Link>
            <Link to="/login" className="text-xs font-bold text-slate-700 hover:text-slate-900" data-testid="landing-login">Log in</Link>
            <Link to={w.hero.cta_href}
              className={`h-9 px-4 rounded-lg font-black uppercase text-[10px] tracking-widest text-white ${accentBg} flex items-center gap-1`}
              data-testid="landing-cta-nav">
              Get started <ArrowRight size={12}/>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
        <div className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${accentPill} rounded-full px-3 py-1 mb-5`} data-testid="landing-tag">
          {wedgeKey === "vendors" ? <Wrench size={12}/> : <Buildings size={12}/>} {w.tag}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl mx-auto leading-[1.05]" data-testid="landing-title">
          {w.hero.title}
        </h1>
        <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-2xl mx-auto" data-testid="landing-sub">{w.hero.sub}</p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to={w.hero.cta_href}
            className={`h-12 px-6 rounded-xl font-black uppercase text-xs tracking-widest text-white ${accentBg} inline-flex items-center gap-2`}
            data-testid="landing-cta-hero">
            {w.hero.cta} <ArrowRight size={14}/>
          </Link>
          <Link to="#demo" className="h-12 px-6 rounded-xl font-black uppercase text-xs tracking-widest bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="landing-cta-demo">
            Watch 3-min demo
          </Link>
        </div>
        <div className="text-xs text-slate-400 mt-4">No credit card. Cancel anytime. Live in 90 seconds.</div>
      </section>

      {/* Pain-point cards */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {w.pains.map((pain, i) => {
            const Icon = pain.icon;
            return (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid={`landing-pain-${i}`}>
                <div className={`w-10 h-10 rounded-lg ${accentPill} flex items-center justify-center mb-3`}>
                  <Icon weight="fill" size={20}/>
                </div>
                <h3 className="font-black text-lg mb-1">{pain.k}</h3>
                <p className="text-sm text-slate-600">{pain.p}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Proof / bullets */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="landing-proof" id="demo">
        <div className={`rounded-3xl ${w.accent === "cyan" ? "bg-slate-900" : "bg-gradient-to-br from-slate-900 to-fuchsia-900"} text-white p-8 sm:p-12`}>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-2">What you get</div>
          <h2 className="text-3xl font-black tracking-tight mb-6">Everything wired. Nothing to configure.</h2>
          <div className="space-y-3">
            {w.proof.map((line, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`landing-proof-${i}`}>
                <CheckCircle weight="fill" size={20} className={`shrink-0 mt-0.5 ${w.accent === "cyan" ? "text-cyan-400" : "text-fuchsia-400"}`}/>
                <div className="text-slate-100">{line}</div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to={w.hero.cta_href}
              className={`h-12 px-6 rounded-xl font-black uppercase text-xs tracking-widest text-slate-900 bg-white hover:bg-slate-100 inline-flex items-center gap-2`}
              data-testid="landing-cta-proof">
              {w.hero.cta} <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-4xl mx-auto px-6 pb-16 text-center">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Pricing</div>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Simple. Per shop. No seats.</h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 text-left">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Starter</div>
            <div className="mt-2 flex items-baseline gap-1">
              <div className="text-4xl font-black">$99</div>
              <div className="text-sm text-slate-500">/ month</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">Up to 5 techs · Unlimited WOs · 20+ integrations</div>
            <Link to={w.hero.cta_href}
              className="mt-4 block w-full h-11 rounded-lg bg-slate-900 hover:bg-black text-white font-black uppercase text-[10px] tracking-widest flex items-center justify-center"
              data-testid="landing-price-starter">Start free trial</Link>
          </div>
          <div className={`rounded-2xl p-6 text-left text-white ${w.accent === "cyan" ? "bg-cyan-600" : "bg-fuchsia-600"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Team</div>
            <div className="mt-2 flex items-baseline gap-1">
              <div className="text-4xl font-black">$249</div>
              <div className="text-sm opacity-70">/ month</div>
            </div>
            <div className="text-xs opacity-90 mt-1">Unlimited techs · Priority support · Custom integration adapters</div>
            <Link to={w.hero.cta_href}
              className="mt-4 block w-full h-11 rounded-lg bg-white text-slate-900 font-black uppercase text-[10px] tracking-widest flex items-center justify-center"
              data-testid="landing-price-team">Start free trial</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-slate-400">© {new Date().getFullYear()} Field Radar · Property Management Radar</div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <Link to="/vendors" className="hover:text-slate-900">Vendors</Link>
            <Link to="/for-property-managers" className="hover:text-slate-900">Property Managers</Link>
            <Link to="/login" className="hover:text-slate-900">Log in</Link>
            <Link to={w.hero.cta_href} className="hover:text-slate-900">Get started</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
